'use strict'
const { createStore, applyMiddleware } = require('redux')
const { default: thunk } = require('redux-thunk')
const uniq_ = require('lodash/uniq')
const mapValues_ = require('lodash/mapValues')
const groupBy_ = require('lodash/groupBy')
const has_ = require('lodash/has')
const get_ = require('lodash/get')
const isEqual_ = require('lodash/isEqual')

const servicesCreator = require('dbsm-common/src/service-container/services')
const { queuedThunkMiddleware } = require('dbsm-common/src/redux/queuedThunk')
const ravenMiddleware = require('dbsm-common/src/raven/middleware')
const {
  UploadButton: uploadButtonSdkType
} = require('dbsm-common/src/componentTypes')
const { SCOPE_TYPES } = require('dbsm-common/src/scopes/consts')

const rootReducer = require('./rootReducer')
const recordActions = require('../records/actions')
const { provider: recordProvider } = require('../records/provider')
const configActions = require('../dataset-config/actions')
const rootActions = require('./actions')
const {
  setDependencies,
  waitForDependencies,
  performHandshake,
  resolveMissingDependencies
} = require('../dependency-resolution/actions')
const adapterApiCreator = require('../adapters/adapterApi')
const {
  provider: adapterApiProvider
} = require('../adapters/adapterApiProvider')
const { applicationDatasetApi, userDatasetApi } = require('./datasetApi')
const eventListenersCreator = require('./eventListeners')
const syncComponentsWithState = require('../side-effects/syncComponentsWithState')
const createBreadcrumbMessage = require('../helpers/createBreadcrumbMessage')
const getFieldType = require('../helpers/getFieldType')
const createScopeStore = require('./scopeStore')
const createScopeFactory = require('./scopeFactory')
const {
  findConnectedComponents,
  createComponentAdapterContexts,
  createDetailsRepeatersAdapterContexts,
  resolveHandshakes
} = require('./processConnectedRoles')
const { extractDependencies } = require('../helpers/filters')
const dependenciesManagerCreator = require('../dependency-resolution/dependenciesManager')
const recordStoreServiceCreator = require('../recordStore/service')
const service = require('dbsm-common/src/async/service')
const queryResults = require('../helpers/queryResults')
const { resolveFilter } = require('../helpers/filters')
const { setConnectedComponents } = require('../connectedComponents/actions')
const { isSameRecord } = require('../recordStore/records')
const {
  serialEffectsMiddleware,
  dispatchProvider
} = require('redux-serial-effects')
const { provider: fireEventProvifer } = require('../helpers/fireEventProvider')

const rootSubscriber = require('./rootSubscriber')

const hasDependencies = (store, controllerConfig) => {
  return (
    extractDependencies(get_(controllerConfig, ['dataset', 'filter'])).length >
    0
  )
}

const waitForControllerDependencies = (store, controllerConfig) => {
  const dependenciesIds = extractDependencies(
    get_(controllerConfig, ['dataset', 'filter'])
  )
  store.dispatch(setDependencies(dependenciesIds))
  return store.dispatch(waitForDependencies(store))
}

const onChangeHandler = (getState, dispatch) => (
  before,
  after,
  componentIdToExclude
) => {
  const currentRecordIndex = rootReducer.selectCurrentRecordIndex(getState())
  const currentRecord = rootReducer.selectCurrentRecord(getState())
  const updatedFields =
    after && before
      ? Object.keys(after).filter(key => !isEqual_(after[key], before[key]))
      : []
  const isSame = after && currentRecord && isSameRecord(currentRecord, after)
  const isBeforeSame =
    before && currentRecord && isSameRecord(currentRecord, before)
  const sameFieldSet =
    after && before && isEqual_(Object.keys(before), Object.keys(after))

  if (isSame && (updatedFields.length > 0 || !sameFieldSet)) {
    dispatch(recordActions.setCurrentRecord(after, currentRecordIndex))
    dispatch(
      recordActions.updateComponentsBasedOnRecord(
        componentIdToExclude,
        updatedFields
      )
    )
  } else if (before === null || after === null) {
    if (isBeforeSame) {
      dispatch(recordActions.refreshCurrentRecord()).then(() =>
        dispatch(
          recordActions.updateComponentsBasedOnRecord(componentIdToExclude)
        )
      )
    }
    dispatch(recordActions.refreshCurrentView())
  }
}

const createRecordStoreService = (wixDataProxy, datasetConfig, Raven) =>
  recordStoreServiceCreator(
    wixDataProxy,
    datasetConfig.collectionName,
    datasetConfig.includes,
    Raven
  )

const createDatasetStore = (services, ravenProxy, uniqueId, fireEvent) => {
  const {
    middleware: serialEffects,
    subscribe,
    registerProviders
  } = serialEffectsMiddleware.withExtraArgument({
    services: services(),
    fireEvent,
    Raven: ravenProxy
  })
  const store = createStore(
    rootReducer,
    undefined,
    applyMiddleware(
      queuedThunkMiddleware(services()),
      thunk.withExtraArgument(services()),
      serialEffects,
      ravenMiddleware(ravenProxy, breadCrumb =>
        Object.assign({}, breadCrumb, {
          message: createBreadcrumbMessage(breadCrumb.message, uniqueId)
        })
      )
    )
  )

  return {
    store,
    subscribe,
    registerProviders
  }
}

const createRecordStore = (
  recordStoreServiceWrapper,
  getState,
  prefetchedData,
  getDependencies,
  controllerId
) => () => {
  const state = getState()
  const config = rootReducer.getConfig(state)
  const pageSize = rootReducer.getCurrentPageSize(state)
  const sort = config.sort
  const filter = resolveFilter(config.filter, getDependencies())

  return filter.matchWith({
    Nothing: () => recordStoreServiceWrapper,
    Just: ({ value }) =>
      recordStoreServiceWrapper.instantiate({
        pageSize,
        sort,
        filter: value,
        seed: queryResults.fromWixDataQueryResults(prefetchedData),
        controllerId
      })
  })
}

function waitForAllChildScopesToBeReady(scopeStore) {
  return Promise.all(
    scopeStore.getAll().map(
      scope =>
        new Promise(resolve => {
          scope.staticExports.onReady(resolve)
        })
    )
  )
}

const createDataset = ({
  controllerConfig,
  connections,
  wixDataProxy,
  wixSdk,
  firePlatformEvent,
  errorReporter,
  platformUtilities,
  prefetchedData,
  ravenProxy,
  errorBoundaries,
  uniqueId,
  isScoped,
  handshakes = [],
  schemaAPI,
  recordStoreServiceWrapper,
  fedopsLogger
}) => {
  const unsubscribeHandlers = []
  const eventListeners = eventListenersCreator(firePlatformEvent)
  const { fireEvent } = eventListeners
  unsubscribeHandlers.push(eventListeners.dispose)

  const services = servicesCreator()
  const { store, subscribe, registerProviders } = createDatasetStore(
    services,
    ravenProxy,
    uniqueId,
    fireEvent
  )
  registerProviders(
    fireEventProvifer(fireEvent, errorBoundaries),
    dispatchProvider(store.dispatch)
  )

  unsubscribeHandlers.push(
    ravenProxy.addDataCallback(() => ({
      [uniqueId]: {
        state: store.getState(),
        connections
      }
    }))
  )

  if (controllerConfig && controllerConfig.dataset) {
    store.dispatch(rootActions.setConfig(controllerConfig.dataset))
  }

  const scopeStore = createScopeStore(ravenProxy)
  services({ scopeStore })
  unsubscribeHandlers.push(
    ravenProxy.addDataCallback(() => ({
      scopes: scopeStore.getAll()
    }))
  )

  const dependenciesManager = dependenciesManagerCreator()
  unsubscribeHandlers.push(dependenciesManager.unsubscribe)

  let dependenciesPromise
  if (hasDependencies(store, controllerConfig)) {
    dependenciesPromise = waitForControllerDependencies(store, controllerConfig)
  }

  const datasetCollectionName = get_(controllerConfig, [
    'dataset',
    'collectionName'
  ])
  if (!recordStoreServiceWrapper) {
    if (datasetCollectionName != null) {
      const recordStoreService = createRecordStoreService(
        wixDataProxy,
        controllerConfig.dataset,
        ravenProxy
      )
      recordStoreServiceWrapper = service.Pending(recordStoreService)
    } else {
      recordStoreServiceWrapper = service.Null()
    }
  }

  unsubscribeHandlers.push(
    recordStoreServiceWrapper.matchWith({
      Null: () => {},
      Pending: ({ service }) =>
        service.onChange(
          onChangeHandler(store.getState, store.dispatch, fireEvent, services)
        )
    })
  )

  const recordStore = createRecordStore(
    recordStoreServiceWrapper,
    store.getState,
    prefetchedData,
    dependenciesManager.get,
    uniqueId
  )

  services({
    wixDataProxy,
    eventListeners,
    dependenciesManager,
    handshakes,
    Raven: ravenProxy,
    recordStore
  })
  services({ fedopsLogger })

  const uniqueRoles = uniq_(connections.map(conn => conn.role))

  const componentAdapterContexts = []
  const appDatasetApi = applicationDatasetApi({
    store,
    recordStore,
    services: services(),
    datasetId: uniqueId,
    errorBoundaries
  })

  const getFieldTypeFunc = fieldName => {
    const schema = schemaAPI.getSchema(datasetCollectionName)
    const referencedCollectionsSchemas = schemaAPI.getReferencedCollectionsSchemas(
      datasetCollectionName
    )
    return getFieldType(schema, referencedCollectionsSchemas)(fieldName)
  }

  registerProviders(recordProvider(recordStore, getFieldTypeFunc))

  const hasPrefetchedData = !!prefetchedData
  if (controllerConfig && !rootReducer.isWriteOnly(store.getState())) {
    store.dispatch(
      recordActions.fetchInitialDataAndSetInitialCurrentRecord(
        hasPrefetchedData,
        errorReporter
      )
    )
  }

  handshakes.forEach(handshake => store.dispatch(performHandshake(handshake)))

  const pageReady = errorBoundaries.applicationCodeZone(async function($w) {
    const { timeoutId } = fedopsLogger.interactionStarted('pageReady')
    const connectedComponents = findConnectedComponents(uniqueRoles, $w)
    store.dispatch(
      setConnectedComponents(
        connectedComponents.map(({ component }) => component.uniqueId)
      )
    )

    // THIS SHOULD HAPPEN SYNCHRONOUSLY AFTER PAGE READY IS CALLED TO KEEP CONTROLLERS RUNNING SEQUENCE
    const controllersToHandshake = resolveHandshakes(
      appDatasetApi,
      connectedComponents
    )
    controllersToHandshake.forEach(({ controller, handshakeInfo }) =>
      controller.handshake(handshakeInfo)
    )

    if (dependenciesPromise) {
      // if by now we are still waiting for dependencies, mark them as resolved
      // since they are guaranteed to perform a handshake with us before our pageReady.
      // A missing dependency can happen in a master-detail scenario where the user
      // deleted the master dataset
      store.dispatch(resolveMissingDependencies())

      await dependenciesPromise
    }

    const dependencies = dependenciesManager.get()

    // scoped datasets are sure to have the schema resolved and therefore don't have to wait
    if (!isScoped) {
      await schemaAPI.loadSchemas()
    }

    const scopeFactory = createScopeFactory(createDataset, {
      controllerConfig,
      connections,
      wixDataProxy,
      wixSdk,
      errorReporter,
      platformUtilities,
      ravenProxy,
      errorBoundaries,
      uniqueId,
      services,
      schemaAPI,
      recordStoreServiceWrapper,
      fedopsLogger
    })

    const adapterApi = () =>
      adapterApiCreator({
        adapterParams: {
          getState: store.getState,
          datasetApi: appDatasetApi,
          wixSdk,
          errorReporter,
          platformUtilities,
          eventListeners,
          roles: uniqueRoles,
          getFieldType: getFieldTypeFunc,
          services,
          errorBoundaries
        },
        dispatch: store.dispatch,
        recordStore,
        componentAdapterContexts
      })

    services({
      adapterApi
    })

    componentAdapterContexts.push(
      ...createComponentAdapterContexts({
        connectedComponents,
        $w,
        adapterApi,
        getFieldType: getFieldTypeFunc,
        ignoreItemsInRepeater: !isScoped,
        dependencies
      })
    )

    if (!isScoped) {
      const detailsRepeatersAdapterContexts = createDetailsRepeatersAdapterContexts(
        connectedComponents,
        getFieldTypeFunc,
        dependencies
      )
      componentAdapterContexts.push(...detailsRepeatersAdapterContexts)
    }

    registerProviders(adapterApiProvider(adapterApi))
    subscribe(rootSubscriber)

    services({
      $w,
      getFieldType: getFieldTypeFunc,
      componentAdapterContexts,
      scopeFactory
    })

    unsubscribeHandlers.push(
      addComponentDataToRaven(componentAdapterContexts, ravenProxy, uniqueId)
    )

    unsubscribeHandlers.push(
      syncComponentsWithState(
        store,
        componentAdapterContexts,
        ravenProxy,
        uniqueId,
        recordStore
      )
    )

    const defaultRecord = getDefaultRecord(componentAdapterContexts)
    store.dispatch(recordActions.setDefaultRecord(defaultRecord))
    if (rootReducer.isWriteOnly(store.getState())) {
      store.dispatch(recordActions.initWriteOnly())
    }

    return store
      .dispatch(recordActions.initAdapters(hasPrefetchedData))
      .then(async () => {
        await waitForAllChildScopesToBeReady(scopeStore)
        store.dispatch(configActions.setIsDatasetReady(true))
        fireEvent('datasetReady')
        fedopsLogger.interactionEnded('pageReady', { timeoutId })
      })
  })

  const userCodeDatasetApi = userDatasetApi({
    store,
    recordStore,
    services: services(),
    datasetId: uniqueId,
    errorBoundaries
  })
  const dynamicExports = (scope /*, $w*/) => {
    switch (scope.type) {
      case SCOPE_TYPES.COMPONENT:
        return userCodeDatasetApi.inScope(
          scope.compId,
          scope.additionalData.itemId
        )
      default:
        return userCodeDatasetApi
    }
  }

  const dispose = () => {
    componentAdapterContexts.splice(0)
    unsubscribeHandlers.forEach(h => h())
  }

  return {
    pageReady: errorBoundaries.applicationCodeZone(pageReady),
    exports: dynamicExports,
    staticExports: userCodeDatasetApi,
    dispose
  }
}

const extractControllerAPI = ({ pageReady, exports }) => ({
  pageReady,
  exports
})
const createDatasetController = (...args) =>
  extractControllerAPI(createDataset(...args))

const getDefaultRecord = componentAdapterContexts => {
  const inputComponentsProps = ['value', 'checked'] //todo: export to constant
  return Object.assign(
    {},
    ...componentAdapterContexts
      .filter(({ component }) => component.type !== uploadButtonSdkType)
      .map(({ component, connectionConfig: { properties } }) =>
        Object.assign(
          {},
          ...inputComponentsProps.map(
            propName =>
              has_(properties, propName)
                ? { [properties[propName].fieldName]: component[propName] }
                : undefined
          )
        )
      )
  )
}

const addComponentDataToRaven = (
  componentAdapterContexts,
  ravenProxy,
  uniqueId
) => {
  const componentIdToRole = mapValues_(
    groupBy_(componentAdapterContexts, cac => cac.component.id),
    cacArray => cacArray.map(cac => cac.role).join()
  )

  return ravenProxy.addDataCallback(() => ({
    [uniqueId]: {
      components: componentIdToRole
    }
  }))
}

module.exports = createDatasetController



// WEBPACK FOOTER //
// ./dataset-controller/controller.js